import { Temporal, toTemporalInstant } from '@earzzai/utils-temporal';
import { Draft } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import type { RootState } from '../store';
import { createStreamClient, DefaultApiOptions } from './emptyApi';
import {
  AttendanceStatus,
  DashboardKanbanSoundEvent,
  KanbanAttendanceEventFragment,
  KanbanCallEventFragment,
  KanbanEventMonitorFragment,
  KanbanSoundEventFragment,
  SubscribeKanbanScreenEventsDocument,
  SubscribeKanbanScreenEventsSubscription,
  SubscribeKanbanScreenEventsSubscriptionVariables
} from './generated/graphqlApi';
import { graphqlApi } from './graphqlApi';

/* eslint-disable @typescript-eslint/no-invalid-void-type */

type MaybeDrafted<T> = T | Draft<T>;

type EventTimestamp = { timestamp: number; missed?: boolean };

export type StreamKanbanEvent =
  | KanbanAttendanceEventFragment
  | KanbanCallEventFragment
  | KanbanSoundEventFragment;

export interface StreamKanbanMonitorTile {
  monitor: KanbanEventMonitorFragment;
  tileState: 'in-progress' | 'attending' | 'attended' | 'missed';
  timestamp: number;
  events: (StreamKanbanEvent & EventTimestamp)[];
  summary: {
    event: StreamKanbanEvent & EventTimestamp;
    hasMissed: boolean;
  }[];
}

export interface StreamKanbanData {
  status: 'initial' | 'loading' | 'connecting' | 'connected' | 'stopped';
  retryAfter: number | null;
  lastUpdated: number | null;
  progressTiles: StreamKanbanMonitorTile[];
  finalizedTiles: StreamKanbanMonitorTile[];
}

export const initialState = Object.freeze({
  status: 'initial',
  lastUpdated: null,
  retryAfter: null,
  progressTiles: [],
  finalizedTiles: []
});

const eventIsForAttended = (event: StreamKanbanEvent): boolean => {
  return (
    event.__typename === 'DashboardKanbanAttendanceEvent' &&
    event.attendanceStatus === AttendanceStatus.Attended
  );
};

const eventIsForKanban = (event: StreamKanbanEvent): boolean => {
  switch (event.__typename) {
    case 'DashboardKanbanAttendanceEvent':
      return event.attendanceStatus !== AttendanceStatus.NotAttended;
    default:
      return true;
  }
};

const updateDraft = (
  draft: MaybeDrafted<StreamKanbanData>,
  event: SubscribeKanbanScreenEventsSubscription['dashboardKanbanEventsCreated']
): MaybeDrafted<StreamKanbanData> => {
  if (!event.monitor) {
    return draft;
  }

  const monitorId = event.monitor.id;
  const eventTime = toTemporalInstant(event.createdAt);

  // Add to the first tile/group we found. If not found, we create new ones.
  if (eventIsForKanban(event)) {
    const foundTile = draft.progressTiles.find(t => t.monitor.id == monitorId);
    const tile: StreamKanbanMonitorTile = foundTile ?? {
      monitor: event.monitor,
      tileState: 'in-progress',
      events: [],
      summary: [],
      timestamp: eventTime.epochMilliseconds
    };
    if (!foundTile) {
      draft.progressTiles.push(tile);
    }

    tile.events.unshift({ ...event, timestamp: eventTime.epochMilliseconds });
    updateTileSummary(tile, eventTime.epochMilliseconds);

    if (event.__typename === 'DashboardKanbanAttendanceEvent') {
      switch (event.attendanceStatus) {
        case AttendanceStatus.Attending:
          tile.tileState = 'attending';
          break;
        case AttendanceStatus.Attended:
          // If this is an attendance, we want to move this tile to the finalizedTiles
          // pile.
          {
            const index = draft.progressTiles.findIndex(
              t => t.monitor.id == monitorId
            );
            const [removedTile] = draft.progressTiles.splice(index, 1);
            if (removedTile) {
              draft.finalizedTiles.unshift(
                ...splitFinalizedTile(
                  removedTile,
                  eventTime.epochMilliseconds
                ).reverse()
              );
            }
          }
          break;
      }
    }
  }

  draft.lastUpdated = eventTime.epochMilliseconds;
  return cleanUpDraft(draft);
};

export const isSimilarEvent = (a: StreamKanbanEvent, b: StreamKanbanEvent) => {
  if (a.__typename !== b.__typename) return false;
  switch (a.__typename) {
    case 'DashboardKanbanAttendanceEvent':
      return a.attendanceStatus === b.attendanceStatus;
    case 'DashboardKanbanCallEvent':
      return true;
    case 'DashboardKanbanSoundEvent':
      return a.sound?.slug === (b as DashboardKanbanSoundEvent).sound?.slug;
  }
  return false;
};

export const updateTileSummary = (
  draft: MaybeDrafted<StreamKanbanMonitorTile>,
  lastUpdated: number
) => {
  draft.summary = [...draft.events].reduce<StreamKanbanMonitorTile['summary']>(
    (prev, event) => {
      const found = prev.find(s => isSimilarEvent(s.event, event));
      if (found) {
        found.hasMissed = found.hasMissed || eventIsMissed(event, lastUpdated);
      } else {
        prev.push({ event, hasMissed: eventIsMissed(event, lastUpdated) });
      }
      return prev;
    },
    []
  );
};

const missedInterval = Temporal.Duration.from({ minutes: 15 }).total(
  'millisecond'
);
const staleInterval = Temporal.Duration.from({ minutes: 15 }).total(
  'millisecond'
);

export const eventIsMissed = (event: EventTimestamp, timestamp: number) => {
  return event.timestamp < timestamp - missedInterval;
};

const allEventsFinalized = (
  tile: StreamKanbanMonitorTile,
  timestamp: number
): boolean => {
  for (const event of tile.events) {
    if (!eventIsForAttended(event) && !eventIsMissed(event, timestamp)) {
      // not missed
      return false;
    }
  }
  return true;
};

export const splitFinalizedTile = (
  tile: StreamKanbanMonitorTile,
  timestamp: number
): StreamKanbanMonitorTile[] => {
  // We want to split the tile into two tiles:
  // 1. tile with events being attended to
  // 2. tile with events that are missed
  let attendanceTime: number | null = null;
  const result: StreamKanbanMonitorTile[] = [];

  const pushSplitTile = (
    tileState: 'missed' | 'attended',
    timestamp: number
  ): StreamKanbanMonitorTile => {
    const newTile = {
      tileState,
      events: [],
      summary: [],
      monitor: tile.monitor,
      timestamp
    };
    result.push(newTile);
    return newTile;
  };

  tile.events.forEach(event => {
    let currentTile: StreamKanbanMonitorTile;
    switch (event.__typename) {
      case 'DashboardKanbanAttendanceEvent':
        if (result.length == 0) {
          currentTile = pushSplitTile(
            eventIsForAttended(event) ? 'attended' : 'missed',
            event.timestamp
          );
          attendanceTime = event.timestamp;
        } else {
          currentTile = result[result.length - 1]!;
        }
        break;
      case 'DashboardKanbanCallEvent':
      case 'DashboardKanbanSoundEvent':
        if (result.length == 0) {
          currentTile = pushSplitTile(
            'missed',
            event.timestamp + missedInterval
          );
        } else {
          currentTile = result[result.length - 1]!;
          if (
            currentTile.tileState == 'attended' &&
            (!attendanceTime || eventIsMissed(event, attendanceTime))
          ) {
            currentTile = pushSplitTile(
              'missed',
              event.timestamp + missedInterval
            );
          }
        }
        break;
      default:
        // unexpected
        return;
    }
    currentTile.events.push(event);
  });

  result.forEach(tile => {
    updateTileSummary(tile, timestamp);
  });

  return result;
};

const cleanUpDraft = (
  draft: MaybeDrafted<StreamKanbanData>
): MaybeDrafted<StreamKanbanData> => {
  const { lastUpdated } = draft;
  if (!lastUpdated) {
    return draft;
  }

  for (const tile of draft.progressTiles) {
    for (const event of tile.events) {
      if (event.missed) {
        // assuming event is sorted by increasing timestamp
        break;
      }

      if (eventIsMissed(event, lastUpdated)) {
        event.missed = true;
      }
    }
  }

  let i = 0;
  while (i < draft.progressTiles.length) {
    const tile = draft.progressTiles[i]!;
    if (allEventsFinalized(tile, lastUpdated)) {
      draft.progressTiles.splice(i, 1);
      draft.finalizedTiles.unshift(
        ...splitFinalizedTile(tile, lastUpdated).reverse()
      );
    } else {
      i++;
    }
  }

  i = 0;
  while (i < draft.finalizedTiles.length) {
    const tile = draft.finalizedTiles[i]!;
    if (tile.timestamp < lastUpdated - staleInterval) {
      draft.finalizedTiles.splice(i, 1);
    } else {
      i++;
    }
  }

  return draft;
};

export const kanbanApi = createApi({
  reducerPath: 'kanbanApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }),
  endpoints: build => ({
    StreamKanban: build.query<StreamKanbanData, void>({
      async queryFn(_args, { dispatch }) {
        const { data } = await dispatch(
          graphqlApi.endpoints.FetchKanbanScreenEvents.initiate(undefined, {
            forceRefetch: true
          })
        );

        let result: MaybeDrafted<StreamKanbanData> = {
          ...initialState,
          progressTiles: [],
          finalizedTiles: []
        };
        for (const event of data?.currentAccount?.dashboard?.kanban?.events ??
          []) {
          result = updateDraft(result, event);
        }

        result.lastUpdated = new Date().getTime();
        return {
          data: cleanUpDraft(result)
        };
      },
      async onCacheEntryAdded(
        _args,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }
      ) {
        const { user } = (getState() as RootState).auth;

        // Should abort in the following scenarios:
        //
        // * Stream client disconnected and retry exceeded. AsyncIterator will
        //   close.
        // * User not looking at the live view.
        // * AsyncIterator closed for any reasons.
        const abortController = new AbortController();
        const signal = abortController.signal;

        const client = createStreamClient({
          getState,
          signal
        });

        await cacheDataLoaded;

        try {
          const subscription =
            client.iterate<SubscribeKanbanScreenEventsSubscription>(
              {
                query: SubscribeKanbanScreenEventsDocument,
                variables: {
                  accountId: String(user!.id)
                } satisfies SubscribeKanbanScreenEventsSubscriptionVariables
              },
              {
                connecting: () => {
                  updateCachedData(draft => {
                    draft.status = 'connecting';
                    draft.retryAfter = null;
                  });
                },
                connected: () => {
                  updateCachedData(draft => {
                    draft.status = 'connected';
                    draft.retryAfter = null;
                  });
                }
              }
            );

          void (async () => {
            try {
              for await (const result of subscription) {
                const event = result.data?.dashboardKanbanEventsCreated;
                if (event) {
                  updateCachedData(draft => {
                    return updateDraft(draft, event);
                  });
                }
              }
            } catch (err) {
              console.error('error updating cache data', err);
            } finally {
              abortController.abort();
            }
          })();

          const timer = setInterval(() => {
            updateCachedData(draft => {
              draft.lastUpdated = new Date().getTime();
              return cleanUpDraft(draft);
            });
          }, 5000);
          signal.addEventListener('abort', () => {
            clearInterval(timer);
          });

          signal.addEventListener('abort', () => {
            updateCachedData(draft => {
              draft.status = 'stopped';
              draft.retryAfter = Temporal.Now.instant().add({
                seconds: 30
              }).epochMilliseconds;
            });
          });

          await cacheEntryRemoved;
        } catch (err) {
          console.error('error handling stream subscription', err);
        } finally {
          abortController.abort();
        }
      }
    })
  }),
  ...DefaultApiOptions
});

export const { useStreamKanbanQuery } = kanbanApi;
